/**
 * 会社の色を画面に差し込む（見た目だけの係）。
 *
 * ログインして会社ごとの設定（`GET /org/branding`）が読めたら、その色から濃淡を作って
 * `<html>` に差し込みます。ログインの前・ログアウトしたあとは、この端末で前に読めた色
 * （theme/remembered.ts）、それも無ければ配る版に組み込んだ色（theme/build-brand.ts）を使い、
 * どちらも無ければ中立の灰色のままです。
 * 画面を1つも描かない部品なので、どこに置いても見た目は変わりません。
 */
import { useEffect, type ReactNode } from 'react';
import { useSession } from '../auth/session-context.js';
import { applyBrandPalette, clearBrandPalette } from './brand.js';
import { buildBrandColor } from './build-brand.js';
import { rememberedBrandColor } from './remembered.js';

export function BrandTheme(): ReactNode {
  const { profile } = useSession();
  // 設定の中身は会社ごとに入れる値なので、文字列でないものが入っていることもありうる。
  // そのときは「色の指定なし」として扱う（画面が真っ白になるより、中立の灰色で動く方がよい）
  const raw: unknown = profile?.branding.color_seed;
  const loaded = typeof raw === 'string' ? raw : '';
  // ログインの前は、この端末で前に読めた色 → 配る版に組み込んだ色 → 灰色、の順
  // （ログインの画面を初回から会社の色で出す・総点検 M1・#329）
  const colorSeed = profile ? loaded : rememberedBrandColor() || buildBrandColor();

  useEffect(() => {
    const root = document.documentElement;
    if (colorSeed.length === 0) {
      clearBrandPalette(root);
      return;
    }
    applyBrandPalette(root, colorSeed);
    return () => clearBrandPalette(root);
  }, [colorSeed]);

  return null;
}
